import { logger } from '@thoughtspot/client-state-util/esm/utils';
import {
    getTotalTrialDays,
    isOrgsOnFTEnabled,
} from '@thoughtspot/feature-service';
import { GlobalAlert } from '@thoughtspot/global-alert';
import { useTranslation } from '@thoughtspot/i18n';
import {
    PeopleIncrementalPropertiesEnum,
    UserAction,
    userActionTypeEnum,
} from '@thoughtspot/metrics';
import { ToastAlertProps } from '@thoughtspot/radiant-react/widgets/alert';
import { Button } from '@thoughtspot/radiant-react/widgets/button';
import { IconColor } from '@thoughtspot/radiant-react/widgets/icon';
import { Horizontal, View } from '@thoughtspot/radiant-react/widgets/layout';
import { SafeHTML } from '@thoughtspot/radiant-react/widgets/safe-html';
import { SelectOption } from '@thoughtspot/radiant-react/widgets/select/base-select';
import { Dropdown } from '@thoughtspot/radiant-react/widgets/select/dropdown/select-dropdown';
import {
    Colors,
    Typography,
} from '@thoughtspot/radiant-react/widgets/typography';
import {
    getSubscriptionStatus,
    getSubscriptionType,
    hasAdminPrivileges,
    is1MindBotEnabled,
    PurchaseOption,
    setSourceToPricingPage,
    SubscriptionStatus,
    SubscriptionType,
} from '@thoughtspot/session-service';
import cx from 'classnames';
import { assign } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useTimeout } from '/@utils/use-timeout';
import { OneMindModal } from '../../1Mind/1Mind-modal.container';
import { InviteSource } from '../../invite-users/invite-users.interface';
import { InviteUsersButtonContainer } from '../../invite-users-v2/invite-users-button/invite-users-button.container';
import { ChecklistModal } from './checklist-modal/checklist-modal';
import { ChecklistItem } from './checklist-modal/checklist-modal.types';
import { TrialInfoProps } from './trial-info';
import styles from './trial-info.module.scss';
import {
    BANNER_BUTTONS,
    BannerButtonData,
    checklistDataTestId,
    discountApplicable,
    getDaysLeftText,
    getDiscountOfferText,
    TrailInfoDataTestId,
    TrialBannerData,
    trialMessageDetails,
} from './trial-info.util';

enum MIXPANEL_BUTTON_TYPE {
    CONTACT_SALES = 'CONTACT_SALES',
    BUY_NOW = 'BUY_NOW',
}

export interface TrialInfoSparkflowProps extends TrialInfoProps {
    sparkflowInsightsData: ChecklistItem[];
    checkedItems: Set<string>;
    checklistOpen: boolean;
    setChecklistOpen: (open: boolean) => void;
    onOpenChecklist: () => void;
    onCloseChecklist: () => void;
    onChecklistTaskButtonClick: (type: string, checkboxId: string) => void;
    headerExpandedItemsIndexes: number[];
    handleSectionHeaderClick: (expandedIndexes: number[]) => void;
    taskExpandedState: Record<string, number[]>;
    handleTaskHeaderClick: (
        sectionId: string,
        expandedIndexes: number[],
    ) => void;
    lastCompletedTaskId: string | null;
    onFooterConnectData: () => void;
    isInviteModalOpen: boolean;
    onInviteTeamButtonClick: () => void;
    onInviteComplete: () => void;
    onWebinarLinkClick: () => void;
    sparkflowWebinarUrl?: string;
}

export const TrialInfoSparkflow: React.FC<TrialInfoSparkflowProps> = ({
    collectEvent,
    updateMixPanelProperty,
    trialDaysLeft,
    purchaseOption,
    goToTeamsPlanOptions,
    teamsNotEnabled,
    isDiscountEnabled,
    startTime,
    endTime,
    percentageDiscount,
    discountOfferDuration,
    goToAdmin,
    expiryTimeStamp,
    timezone,
    locale,
    isCopilotEnabled = false,
    isSpotterFullExperienceEnabled = true,
    sparkflowInsightsData,
    checkedItems,
    checklistOpen,
    setChecklistOpen,
    onOpenChecklist,
    onCloseChecklist,
    onChecklistTaskButtonClick,
    headerExpandedItemsIndexes,
    handleSectionHeaderClick,
    taskExpandedState,
    handleTaskHeaderClick,
    lastCompletedTaskId,
    onFooterConnectData,
    isInviteModalOpen,
    onInviteTeamButtonClick,
    onInviteComplete,
    onWebinarLinkClick,
    sparkflowWebinarUrl,
}) => {
    const { t } = useTranslation();

    const checklistButtonRef = useRef<HTMLButtonElement>(null);

    const plansOptions =
        purchaseOption !== PurchaseOption.BUY_NOW || teamsNotEnabled;
    const isSubscriptionCancelled =
        getSubscriptionStatus() === SubscriptionStatus.CANCELED;
    const isFreeTrialEnding =
        getSubscriptionType() === SubscriptionType.FREE_TRIAL &&
        trialDaysLeft < 3;
    // discount offer banner logic
    const isNewDiscountApplicable =
        isDiscountEnabled &&
        !isSubscriptionCancelled &&
        !plansOptions &&
        discountApplicable(startTime, endTime);

    const [isDiscountApplicable, setIsDiscountApplicable] = useState(
        isNewDiscountApplicable,
    );
    const [bannerText, setBannerText] = useState('');
    const [showOneMindModal, setShowOneMindModal] = useState<boolean>(false);
    const [selectedOption, setSelectedOption] = useState<string[]>([]);

    const current_date = Date.now();
    const expiry_date = new Date(expiryTimeStamp);
    const { remainingHours, time, formatedDate } = trialMessageDetails(
        current_date,
        expiry_date,
        timezone,
        locale,
    );
    const daysLeft = getDaysLeftText(
        trialDaysLeft,
        isSubscriptionCancelled,
        remainingHours,
        time,
        formatedDate,
        t,
        true,
    );

    const developerEditionBannerText = assign(
        t('dev_edition_banner_text', {
            days: trialDaysLeft,
        }),
    );

    const copilotTrialBannerText = assign(
        t('copilot.trial.banner.msg', {
            days: trialDaysLeft,
        }),
    );

    const copilotTrialBannerSecondText = assign(
        t('copilot.trial.banner.second.msg', {
            days: trialDaysLeft,
        }),
    );
    const [toastAlert, updateToastAlert] = React.useState<ToastAlertProps>({
        isVisible: false,
        message: '',
    });

    const headerClassName = cx(
        styles.trialInfoHeader,
        styles.sparkFlowContainer,
    );
    const iconColor = IconColor.BLUE;

    const diffInDiscountStartTime = startTime - current_date;
    const diffInDiscountEndTime = endTime - current_date;

    const handleBannerText = () => {
        const isNewDiscountApplicable =
            isDiscountEnabled &&
            !isSubscriptionCancelled &&
            !plansOptions &&
            discountApplicable(startTime, endTime);
        setIsDiscountApplicable(isNewDiscountApplicable);
        const discountBannerText = getDiscountOfferText(
            endTime,
            percentageDiscount,
            discountOfferDuration,
            t,
        );
        const newBannerText = isNewDiscountApplicable
            ? discountBannerText
            : daysLeft;
        setBannerText(newBannerText);
    };

    useEffect(() => {
        handleBannerText();
    }, [startTime, endTime]);

    useTimeout(
        () => {
            const applyDiscount =
                isDiscountEnabled &&
                !isSubscriptionCancelled &&
                !plansOptions &&
                discountApplicable(startTime, endTime);

            if (applyDiscount) {
                const newBannerText = getDiscountOfferText(
                    endTime,
                    percentageDiscount,
                    discountOfferDuration,
                    t,
                );
                setBannerText(newBannerText);
                setIsDiscountApplicable(true);
            }
        },
        {
            delay: diffInDiscountStartTime,
            shouldCall:
                diffInDiscountStartTime > 0 &&
                isDiscountEnabled &&
                !isSubscriptionCancelled,
        },
    );

    useTimeout(
        () => {
            setBannerText(daysLeft);
            setIsDiscountApplicable(false);
        },
        {
            delay: diffInDiscountEndTime,
            shouldCall: diffInDiscountEndTime > 0,
        },
    );

    const mixPanelInfo = {
        totalDaysInTrial: getTotalTrialDays(),
        daysLeftInTrial: daysLeft,
        bannerType: iconColor,
        buttonType: plansOptions
            ? MIXPANEL_BUTTON_TYPE.CONTACT_SALES
            : MIXPANEL_BUTTON_TYPE.BUY_NOW,
        startTime,
        endTime,
        percentageDiscount,
        discountOfferDuration,
    };
    collectEvent(
        new UserAction(userActionTypeEnum.TRIAL_EXPIRY_MESSAGE_SEEN),
        mixPanelInfo,
    );

    const handleContactSalesButtonClick = () => {
        updateMixPanelProperty(
            PeopleIncrementalPropertiesEnum.totalTrialExpiryBtnCLicks,
            1,
        );
        collectEvent(
            new UserAction(userActionTypeEnum.TRIAL_EXPIRY_MESSAGE_CLICKED),
            mixPanelInfo,
        );
        try {
            (window as any).Intercom('show');
        } catch (error) {
            logger.error('Error showing Intercom', error);
        }
    };

    const handleViewPlanOptionsButtonClick = () => {
        updateMixPanelProperty(
            PeopleIncrementalPropertiesEnum.totalTrialExpiryBtnCLicks,
            1,
        );
        if (isSubscriptionCancelled) {
            collectEvent(
                new UserAction(
                    userActionTypeEnum.TEAM_REACTIVATE_SUBSCRIPTION_MESSAGE_CLICKED,
                ),
                {
                    daysLeftUntilPlanCanBeReactivated: daysLeft,
                    bannerType: iconColor,
                },
            );
        } else if (discountApplicable) {
            collectEvent(
                new UserAction(userActionTypeEnum.DISCOUNT_EXPERIMENTATION),
                mixPanelInfo,
            );
        } else {
            collectEvent(
                new UserAction(userActionTypeEnum.TRIAL_EXPIRY_MESSAGE_CLICKED),
                mixPanelInfo,
            );
        }
        setSourceToPricingPage(
            isDiscountEnabled ? 'Discount Banner' : 'Free Trail Banner',
        );
        if (isOrgsOnFTEnabled() && hasAdminPrivileges()) {
            goToAdmin('pricing', true);
        } else goToTeamsPlanOptions();
    };

    const getButtonDetails = () => {
        if (isFreeTrialEnding) {
            return {
                id: 'upgrade_now',
                text: t('upgrade_cluster'),
                onClick: handleContactSalesButtonClick,
                dataTestId: TrailInfoDataTestId.ContactSales,
            };
        }
        if (isSubscriptionCancelled) {
            return {
                id: 'reactivate',
                text: t('reactivate_subscription'),
                onClick: handleViewPlanOptionsButtonClick,
                dataTestId: TrailInfoDataTestId.Reactivate,
            };
        }

        if (plansOptions) {
            return {
                id: 'upgrade_cluster',
                text: t('contact_sales'),
                onClick: handleContactSalesButtonClick,
                dataTestId: TrailInfoDataTestId.ContactSales,
            };
        }
        return {
            id: 'plan_options',
            text: isDiscountApplicable
                ? t('trial_banner_buy_now')
                : t('plan_options'),
            onClick: handleViewPlanOptionsButtonClick,
            dataTestId: TrailInfoDataTestId.PlanOptions,
        };
    };

    const getTrialBannerData = (): TrialBannerData => {
        const contactSalesButtonDetails = getButtonDetails();

        return {
            [BANNER_BUTTONS.INVITE_TEAM]: {
                label: t('freetrial.banner.invite.team'),
                onClick: onInviteTeamButtonClick,
                dataTestId: TrailInfoDataTestId.InviteTeam,
            },
            [BANNER_BUTTONS.CONTACT_SALES]: {
                label: contactSalesButtonDetails.text,
                id: contactSalesButtonDetails.id,
                onClick: contactSalesButtonDetails.onClick,
                dataTestId: contactSalesButtonDetails.dataTestId,
            },
            [BANNER_BUTTONS.BOOK_EXPERT_HELP]: {
                label: t('edge.convAssist.banner.call'),
                onClick: () => setShowOneMindModal(true),
                dataTestId: TrailInfoDataTestId.BookExpertHelp,
            },
        };
    };

    const renderBannerButton = (
        button: BannerButtonData,
        withWrapper = false,
    ) => {
        const content = (
            <Typography
                variant="p"
                className={cx(styles.ctaLinkButton, styles.bannerLink)}
                onClick={button.onClick}
                data-testid={button.dataTestId}
                color={Colors.white}
            >
                {button.label}
            </Typography>
        );

        // Contact sales has View wrapper to pass id for pendo
        return withWrapper && button.id ? (
            <View id={button.id}>{content}</View>
        ) : (
            content
        );
    };

    const renderTrialBannerButtons = (
        shouldShowSalesButton: boolean,
        shouldShowBookExpertHelpButton: boolean,
    ) => {
        const trialButtons = getTrialBannerData();
        return (
            <Horizontal
                className={cx(
                    styles.spotterRightLinks,
                    styles.bannerButtonContainer,
                )}
                data-testid={TrailInfoDataTestId.BannerButtonContainer}
            >
                {renderBannerButton(trialButtons[BANNER_BUTTONS.INVITE_TEAM])}
                {shouldShowSalesButton &&
                    renderBannerButton(
                        trialButtons[BANNER_BUTTONS.CONTACT_SALES],
                        true,
                    )}
                {shouldShowBookExpertHelpButton &&
                    renderBannerButton(
                        trialButtons[BANNER_BUTTONS.BOOK_EXPERT_HELP],
                    )}
            </Horizontal>
        );
    };

    const buttonToDropdownOption = (button: BannerButtonData) => ({
        label: button.label,
        id: button.dataTestId || button.id,
        onSelect: button.onClick,
    });

    // To be used on screen size < 1000px
    const renderTrialBannerDropdown = (
        shouldShowSalesButton: boolean,
        shouldShowBookExpertHelpButton: boolean,
    ) => {
        const trialButtons = getTrialBannerData();

        const options = [
            buttonToDropdownOption(trialButtons[BANNER_BUTTONS.INVITE_TEAM]),
            ...(shouldShowSalesButton
                ? [
                      buttonToDropdownOption(
                          trialButtons[BANNER_BUTTONS.CONTACT_SALES],
                      ),
                  ]
                : []),
            ...(shouldShowBookExpertHelpButton
                ? [
                      buttonToDropdownOption(
                          trialButtons[BANNER_BUTTONS.BOOK_EXPERT_HELP],
                      ),
                  ]
                : []),
        ];

        return (
            <Dropdown
                customCurrentSelectionDisplay={
                    <Typography
                        variant="body-normal"
                        className={styles.dropdownPlaceholder}
                        ellipsis={{ rows: 1 }}
                        color={Colors.white}
                    >
                        {t('freetrial.banner.resources')}
                    </Typography>
                }
                optionsGroups={[{ options }]}
                onChange={option => {
                    setSelectedOption([(option as SelectOption)?.id]);
                }}
                onMenuToggle={isOpen => {
                    if (!isOpen) {
                        // Reset selection when dropdown closes
                        setSelectedOption([]);
                    }
                }}
                selectedOptions={selectedOption}
                disabled={false}
                classList={{ selectHeadClass: styles.trialBannerDropdown }}
            />
        );
    };

    const getTrialBannerButton = () => {
        if (isCopilotEnabled) {
            return <></>;
        }

        const shouldShowSalesButton =
            getSubscriptionType() !== SubscriptionType.TEAMS_EDITION &&
            (!isOrgsOnFTEnabled() || hasAdminPrivileges());
        const shouldShowBookExpertHelpButton =
            getSubscriptionType() !== SubscriptionType.TEAMS_EDITION;

        // Visibility is handled by CSS
        return (
            <Horizontal>
                {renderTrialBannerButtons(
                    shouldShowSalesButton,
                    shouldShowBookExpertHelpButton,
                )}
                {renderTrialBannerDropdown(
                    shouldShowSalesButton,
                    shouldShowBookExpertHelpButton,
                )}
            </Horizontal>
        );
    };

    const getTrialBannerText: any = () => {
        if (getSubscriptionType() === SubscriptionType.TEAMS_EDITION) {
            return <></>;
        }
        if (isCopilotEnabled) {
            if (isSpotterFullExperienceEnabled) {
                return <SafeHTML html={copilotTrialBannerSecondText} />;
            }
            return <SafeHTML html={copilotTrialBannerText} />;
        }

        if (getSubscriptionType() === SubscriptionType.DEVELOPER_EDITION) {
            return (
                <Typography
                    variant="p"
                    noMargin
                    color={Colors.white}
                    className={styles.bannerText}
                >
                    {developerEditionBannerText}
                </Typography>
            );
        }
        return (
            <Typography
                variant="p"
                noMargin
                color={Colors.white}
                className={styles.bannerText}
            >
                {bannerText}
            </Typography>
        );
    };
    const oneMindBotEnabled = is1MindBotEnabled();

    return (
        <>
            {oneMindBotEnabled && showOneMindModal && (
                <OneMindModal
                    showOneMindModal={showOneMindModal}
                    setShowOneMindModal={setShowOneMindModal}
                    hideBot
                />
            )}
            {checklistOpen && (
                <ChecklistModal
                    checklistItems={sparkflowInsightsData}
                    checklistOpen={checklistOpen}
                    setChecklistOpen={setChecklistOpen}
                    checklistButtonRef={checklistButtonRef}
                    checkedItems={checkedItems}
                    onChecklistTaskButtonClick={onChecklistTaskButtonClick}
                    headerExpandedItemsIndexes={headerExpandedItemsIndexes}
                    handleSectionHeaderClick={handleSectionHeaderClick}
                    taskExpandedState={taskExpandedState}
                    handleTaskHeaderClick={handleTaskHeaderClick}
                    onCloseChecklist={onCloseChecklist}
                    lastCompletedTaskId={lastCompletedTaskId}
                    onInviteTeam={onInviteTeamButtonClick}
                    onConnectData={onFooterConnectData}
                    onWebinarLinkClick={onWebinarLinkClick}
                    sparkflowWebinarUrl={sparkflowWebinarUrl}
                />
            )}
            {isInviteModalOpen && (
                <InviteUsersButtonContainer
                    onEmailAdd={null}
                    updateToastAlert={updateToastAlert}
                    isInviteLinkCall
                    onInviteComplete={onInviteComplete}
                    shouldCollectInviteBtnEvent
                    onInviteBtnClickUserAction={
                        new UserAction(
                            userActionTypeEnum.INVITE_TEAMMATES_FT_BANNER,
                        )
                    }
                    source={InviteSource.FT_BANNER}
                />
            )}
            <View className={headerClassName}>
                <Horizontal
                    vAlignContent="center"
                    className={cx(
                        styles.trialInfoContainer,
                        styles.sparkFlowContentContainer,
                    )}
                    data-testid={TrailInfoDataTestId.SparkFlow}
                >
                    {getTrialBannerButton()}
                    {getTrialBannerText()}
                    <Button
                        type="secondary"
                        text={t('freetrial.banner.checklist.exploreitems')}
                        onClick={onOpenChecklist}
                        buttonRef={checklistButtonRef}
                        data-tour-id="tour_checklist-explore-features-btn"
                        data-testid={
                            checklistDataTestId.CHECKLIST_BANNER_BUTTON
                        }
                        config={{
                            classes: {
                                button: styles.checklistButtonEl,
                                text: styles.checklistButtonText,
                            },
                        }}
                    />
                </Horizontal>
                <span className="calendar-id" />
            </View>
            <GlobalAlert.Toast {...toastAlert} />
        </>
    );
};
